import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { getAgencyConfig } from '../services/agencyConfig';

/**
 * Whether this agency allows advancing a chart without a signature.
 *
 * The setting itself defaults to *on*, but this hook still reports `false`
 * until the config resolves, and on failure. That is deliberate, and it is a
 * statement about knowledge rather than about policy: an agency that opted out
 * would otherwise see the moves offered for the moment before the fetch lands,
 * and a click in that window earns a 403 from the backend gate. A button that
 * appears a beat late is a smaller harm than one that appears and then refuses.
 *
 * The cost is that the common case — an agency on the default — now sees that
 * beat on every panel mount, because nothing caches the config. Worth revisiting
 * if it reads as flicker.
 */
export function useAllowSubmitWithoutSignature(): boolean {
  const agencyId = useStore(useShallow(s => s.user?.agency?.id));
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!agencyId) {
      setAllowed(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (!cancelled) {
          setAllowed(Boolean(cfg.allow_submit_without_signature));
        }
      } catch {
        if (!cancelled) setAllowed(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  return allowed;
}
